import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { brand } from 'utils/designTokens';

/**
 * Reusable export actions button group for reports (Export CSV, Export Excel, Print).
 */
export default function ExportActions({ data = [], columns = [], title = 'Report' }) {
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const downloadCSV = () => {
    if (!data || !data.length) {
      setOpenSnackbar(true);
      return;
    }

    // Header line (skip columns without headerName or field)
    const activeColumns = columns.filter((col) => col.field && col.headerName);
    const headers = activeColumns.map((col) => col.headerName);
    const csvRows = [headers.join(',')];

    // Data lines
    for (const row of data) {
      const values = activeColumns.map((col) => {
        let val = row[col.field];
        if (typeof col.valueGetter === 'function') {
          val = col.valueGetter(val, row);
        }
        if (typeof col.valueFormatter === 'function') {
          val = col.valueFormatter({ value: val, row });
        }

        // Deep extract store name or customer name if object
        if (val && typeof val === 'object') {
          val = val.title_en || val.name || JSON.stringify(val);
        }

        // Clean string, escape double quotes
        const stringVal = val === null || val === undefined ? '' : String(val);
        const escaped = stringVal.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }

    // Add UTF-8 BOM so Excel opens it with correct encoding
    const csvContent = '\uFEFF' + csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `${title.toLowerCase().replace(/\s+/g, '-')}.csv`;
    saveAs(blob, fileName);
  };

  const downloadExcel = () => {
    if (!data || !data.length) {
      setOpenSnackbar(true);
      return;
    }

    const activeColumns = columns.filter((col) => col.field && col.headerName);
    const headers = activeColumns.map((col) => col.headerName);

    // Map rows to objects with headers as keys
    const sheetData = data.map((row) => {
      const obj = {};
      activeColumns.forEach((col) => {
        let val = row[col.field];
        if (typeof col.valueGetter === 'function') {
          val = col.valueGetter(val, row);
        }
        if (typeof col.valueFormatter === 'function') {
          val = col.valueFormatter({ value: val, row });
        }

        if (val && typeof val === 'object') {
          val = val.title_en || val.name || JSON.stringify(val);
        }

        obj[col.headerName] = val === null || val === undefined ? '' : val;
      });
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    const fileName = `${title.toLowerCase().replace(/\s+/g, '-')}.xlsx`;
    saveAs(blob, fileName);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <ButtonGroup variant="contained" size="small" sx={{ height: 38 }}>
        <Button
          onClick={downloadCSV}
          startIcon={<DownloadOutlinedIcon />}
          sx={{
            bgcolor: brand.primary,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { bgcolor: brand.primaryDark }
          }}
        >
          Export CSV
        </Button>
        <Button
          onClick={downloadExcel}
          startIcon={<DownloadOutlinedIcon />}
          sx={{
            bgcolor: '#2e7d32', // Excel green color
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { bgcolor: '#1b5e20' }
          }}
        >
          Export Excel
        </Button>
        <Button
          onClick={handlePrint}
          startIcon={<LocalPrintshopOutlinedIcon />}
          sx={{
            bgcolor: brand.primaryLight,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { bgcolor: brand.primary }
          }}
          disabled={!data || !data.length}
        >
          Print
        </Button>
      </ButtonGroup>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="warning" sx={{ width: '100%' }}>
          No data available for export
        </Alert>
      </Snackbar>
    </>
  );
}

ExportActions.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  title: PropTypes.string
};
